import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Sparkles, ArrowLeft, ArrowRight, Check, Loader2, Building2, Rocket, Home, LayoutDashboard, Share2, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { ImageUpload } from '../ui/ImageUpload';

/**
 * First-login "Bienvenida" wizard for new agency admins. Shows exactly once per
 * user: profiles.onboarding_completed_at is stamped on finish OR skip (works
 * across devices — mobile reads the same column). Modeled on WebsiteSetupWizard.
 */

const STEPS = [
  { key: 'bienvenida', label: 'Bienvenida', icon: Sparkles },
  { key: 'marca', label: 'Tu marca', icon: Building2 },
  { key: 'listo', label: 'Primer paso', icon: Rocket },
] as const;

export const WelcomeWizard: React.FC<{ theme: string }> = ({ theme }) => {
  const isLight = theme !== 'dark';
  const navigate = useNavigate();
  const { profile, agency, isImpersonating, refreshAuthData } = useAuth();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const [logoUrl, setLogoUrl] = useState<string>(agency?.logo_url || '');
  const [phone, setPhone] = useState<string>(agency?.phone || '');

  const visible =
    !dismissed &&
    !isImpersonating &&
    !!profile &&
    profile.role === 'agency_admin' &&
    !profile.onboarding_completed_at;

  if (!visible) return null;

  const markDone = async () => {
    if (!supabase || !profile) return;
    await supabase
      .from('profiles')
      .update({ onboarding_completed_at: new Date().toISOString() })
      .eq('id', profile.id);
    await refreshAuthData();
  };

  const saveBrand = async () => {
    if (!supabase || !profile?.agency_id) return;
    const patch: Record<string, string> = {};
    if (logoUrl && logoUrl !== (agency?.logo_url || '')) patch.logo_url = logoUrl;
    if (phone.trim() && phone.trim() !== (agency?.phone || '')) patch.phone = phone.trim();
    if (Object.keys(patch).length > 0) {
      // RPC, not a direct update: agencies has no agency_admin UPDATE policy
      // (a direct .update() silently no-ops — 20260729212000).
      const { error } = await supabase.rpc('update_agency_profile', { p_patch: patch });
      if (error) throw error;
    }
  };

  const finish = async (destination: string | null) => {
    setSaving(true);
    try {
      await saveBrand();
      await markDone();
      setDismissed(true);
      if (destination) navigate(destination);
    } catch {
      alert('No se pudo guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await markDone();
    } finally {
      setDismissed(true);
      setSaving(false);
    }
  };

  const inputCls = clsx(
    'w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
    isLight ? 'bg-white border-slate-200 text-slate-900' : 'bg-white/5 border-white/10 text-white',
  );
  const labelCls = clsx('text-sm font-semibold mb-1.5 block', isLight ? 'text-slate-700' : 'text-slate-300');
  const textCls = clsx('text-sm', isLight ? 'text-slate-600' : 'text-slate-300');

  const firstName = (profile.full_name || '').split(' ')[0] || 'colega';
  const planName = agency?.subscription_plan || '';

  const bullets = [
    { icon: Home, text: 'Publica tus propiedades en los portales más visitados de Guatemala.' },
    { icon: Share2, text: 'Comparte a la Red de Intercambio y cierra negocios con otras agencias.' },
    { icon: Users, text: 'Gestiona prospectos, requerimientos y tu equipo en un solo lugar.' },
  ];

  const stepBody = (() => {
    switch (STEPS[step].key) {
      case 'bienvenida':
        return (
          <div className="space-y-5">
            <p className={clsx('text-lg font-bold', isLight ? 'text-slate-900' : 'text-white')}>
              ¡Bienvenido a Intercambio Inmobiliario, {firstName}! 🎉
            </p>
            <p className={textCls}>
              {planName
                ? <>Tu membresía <strong>{planName}</strong> ya está activa. Esto es lo que puedes hacer desde hoy:</>
                : 'Tu cuenta ya está activa. Esto es lo que puedes hacer desde hoy:'}
            </p>
            <div className="space-y-3">
              {bullets.map(({ icon: Icon, text }) => (
                <div key={text} className={clsx('flex items-start gap-3 p-3 rounded-xl border', isLight ? 'bg-slate-50 border-slate-100' : 'bg-white/5 border-white/10')}>
                  <Icon size={18} className="text-indigo-500 shrink-0 mt-0.5" />
                  <span className={textCls}>{text}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">Te tomará menos de un minuto dejar todo listo.</p>
          </div>
        );
      case 'marca':
        return (
          <div className="space-y-4">
            <p className={textCls}>
              Tu logo aparece en tus publicaciones y en tu perfil ante otras agencias. Súbelo ahora — puedes cambiarlo después en Configuración.
            </p>
            <ImageUpload
              value={logoUrl}
              onChange={setLogoUrl}
              bucket="agency-assets"
              folder={profile.agency_id ? `${profile.agency_id}/logos` : 'logos'}
              label={logoUrl ? 'Logo de tu agencia' : 'Sube tu logo'}
              variant="logo"
              theme={theme as any}
            />
            <div>
              <label className={labelCls}>Teléfono / WhatsApp de la agencia</label>
              <input
                className={inputCls}
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+502 0000 0000"
              />
              <p className="text-xs text-slate-400 mt-1.5">Así te contactan los interesados en tus propiedades.</p>
            </div>
          </div>
        );
      case 'listo':
        return (
          <div className="space-y-5">
            <p className={clsx('text-lg font-bold', isLight ? 'text-slate-900' : 'text-white')}>
              ¡Todo listo! ¿Por dónde empezamos?
            </p>
            <p className={textCls}>
              Las agencias que publican su primera propiedad el primer día reciben coincidencias mucho más rápido.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => finish('/propiedades/nueva')}
                disabled={saving}
                className="flex flex-col items-center gap-2 p-5 rounded-2xl text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-60"
              >
                <Home size={24} />
                <span className="font-bold text-sm">Publicar mi primera propiedad</span>
                <span className="text-xs opacity-80">Recomendado</span>
              </button>
              <button
                onClick={() => finish(null)}
                disabled={saving}
                className={clsx(
                  'flex flex-col items-center gap-2 p-5 rounded-2xl border transition-all disabled:opacity-60',
                  isLight ? 'border-slate-200 hover:border-indigo-300 text-slate-700' : 'border-white/10 hover:border-indigo-500/50 text-slate-200',
                )}
              >
                <LayoutDashboard size={24} />
                <span className="font-bold text-sm">Explorar el panel</span>
                <span className="text-xs opacity-60">A mi ritmo</span>
              </button>
            </div>
          </div>
        );
    }
  })();

  const isLast = step === STEPS.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* No close-on-backdrop: a stray click would stamp the once-ever flag. */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className={clsx(
        'relative w-full max-w-xl max-h-[90vh] flex flex-col rounded-2xl border shadow-2xl overflow-hidden',
        isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10',
      )}>
        {/* Header */}
        <div className={clsx('flex items-center gap-3 px-6 py-4 border-b', isLight ? 'border-slate-100' : 'border-white/10')}>
          <div className="w-9 h-9 rounded-xl bg-indigo-600 shadow-md flex items-center justify-center text-white shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="flex-1">
            <h2 className={clsx('font-bold', isLight ? 'text-slate-900' : 'text-white')}>Bienvenida a bordo</h2>
            <p className="text-xs text-slate-400">Tu cuenta lista en 3 pasos.</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className={clsx('flex items-center justify-center gap-1 px-6 py-3 border-b', isLight ? 'border-slate-100' : 'border-white/10')}>
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <React.Fragment key={s.key}>
                {i > 0 && <div className={clsx('w-8 h-0.5', done || active ? 'bg-indigo-500' : (isLight ? 'bg-slate-200' : 'bg-white/10'))} />}
                <button
                  onClick={() => i < step && setStep(i)}
                  className={clsx(
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold transition-all',
                    active ? 'bg-indigo-600 text-white shadow' :
                    done ? 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-white/5' :
                    'text-slate-400',
                  )}
                  disabled={i > step}
                >
                  {done ? <Check size={13} /> : <Icon size={13} />}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 custom-scrollbar">
          {stepBody}
        </div>

        {/* Footer */}
        <div className={clsx('flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-t', isLight ? 'border-slate-100' : 'border-white/10')}>
          <button
            onClick={skip}
            disabled={saving}
            className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors whitespace-nowrap disabled:opacity-60"
          >
            Omitir por ahora
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className={clsx(
                  'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition-all',
                  isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-300 hover:bg-white/10',
                )}
              >
                <ArrowLeft size={16} /> Anterior
              </button>
            )}
            {!isLast && (
              <button
                onClick={() => setStep(step + 1)}
                disabled={saving}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                Siguiente
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
